/* eslint-disable @typescript-eslint/no-explicit-any */
import { useContext } from 'react';
import { serverData } from '../../organisms/HotelOverview/HotelOverview';
import { useHotelOverviewServerDataStore } from '../../modules/store/hotelOverviewServerDataStore';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';
import { FEATURE_FLAGS, FLAG_TYPE } from '@marriott/mi-feature-flag-utils';
import { ComponentProps } from '../../organisms/HotelOverview/HotelOverview.types';

interface UseConditionalServerDataHotelOverviewOptions {
  /**
   * Specify which component is using this hook to determine the correct feature flag
   * 'adf' will check EXPERIENCE_MCOM_SHOP_ADF_ZUSTAND (for AvailabilityCalendar and CalendarWrapper)
   * 'serp' will check EXPERIENCE_MCOM_SHOP_SERP_ZUSTAND (for all other components)
   */
  flagType?: 'adf' | 'serp';
}

/**
 * Hook that conditionally returns data from either the Zustand store or React Context
 * for HotelOverview serverData based on feature flags, maintaining the same API surface for seamless migration
 */
export const useConditionalServerDataHotelOverview = (
  options: UseConditionalServerDataHotelOverviewOptions = { flagType: 'serp' }
): ComponentProps & any => {
  const { flagType = 'serp' } = options;

  // Get feature flags from the client environment store
  const featureFlags = useClientEnvVarsStore(state => state.envVarsObject['featureFlags']) as unknown as Record<
    string,
    FLAG_TYPE
  >;

  // Determine which feature flag to check based on component type
  const shouldUseZustand =
    flagType === 'adf'
      ? featureFlags?.[FEATURE_FLAGS.EXPERIENCE_MCOM_SHOP_ADF_ZUSTAND] ?? false
      : featureFlags?.[FEATURE_FLAGS.EXPERIENCE_MCOM_SHOP_SERP_ZUSTAND] ?? false;

  // Get data from React Context (fallback)
  const hotelOverviewContext = useContext(serverData);

  // Get Zustand store functions and state
  const zustandStore = useHotelOverviewServerDataStore();
  const {
    updateServerDataProperty,
    updateHotelOverviewServerDataState,
    resetHotelOverviewServerDataStore,
    ...zustandState
  } = zustandStore;

  if (shouldUseZustand) {
    // Return Zustand store data with methods that match HotelOverview serverData Context API
    // The store is initialized independently in the provider component
    return {
      ...zustandState,
      // Additional methods from Zustand store for advanced usage
      updateServerDataProperty,
      updateHotelOverviewServerDataState,
      resetHotelOverviewServerDataStore,
      jsonContent: zustandState.model,
    };
  } else {
    // Return React Context data as-is
    return hotelOverviewContext || {};
  }
};

/**
 * Convenience hook specifically for ADF components (AvailabilityCalendar, CalendarWrapper)
 * that checks EXPERIENCE_MCOM_SHOP_ADF_ZUSTAND flag for HotelOverview serverData
 */
export const useConditionalServerDataHotelOverviewADF = () => {
  return useConditionalServerDataHotelOverview({ flagType: 'adf' });
};

/**
 * Convenience hook specifically for SERP components
 * that checks EXPERIENCE_MCOM_SHOP_SERP_ZUSTAND flag for HotelOverview serverData
 */
export const useConditionalServerDataHotelOverviewSERP = () => {
  return useConditionalServerDataHotelOverview({ flagType: 'serp' });
};
